// victoria-client/src/views/items/detail/stock-chart/useStockChartData.ts
import { useMemo, useState } from "react";

import {
  useRunProjection,
  useStockHistory,
  useStockProjection,
} from "@/api/stockChart";
import type { ViewRange } from "@/components/timeseries-chart/types";
import { parseDate } from "@/utils/formatters";

const DAY_MS = 24 * 60 * 60 * 1000;
const YEAR_MS = 365 * DAY_MS;
const HISTORY_SPAN_MS = 5 * YEAR_MS;
// Stable empty fallback so the memos below don't recompute on every render.
const NONE: never[] = [];

/** Fetches history + projection for one node (item, location[, run]) and owns the
 *  chart's view-range state. Series → datasets transformation lives in
 *  `./datasets`; this hook performs none of it. */
export function useStockChartData(
  itemId: number,
  locationId: number,
  runId?: number,
  without?: number,
  asOf?: string,
) {
  // Frozen at mount: a per-render Date.now() would change `from` every render
  // and thrash the history query cache.
  const historyFrom = useMemo(
    () => new Date(Date.now() - HISTORY_SPAN_MS).toISOString().slice(0, 10),
    [],
  );
  const historyQuery = useStockHistory(itemId, locationId, historyFrom);
  const runQuery = useRunProjection(runId ?? 0, itemId, locationId, without, {
    enabled: runId !== undefined,
  });
  const liveQuery = useStockProjection(itemId, locationId, asOf, {
    enabled: runId === undefined,
  });
  const projectionQuery = runId === undefined ? liveQuery : runQuery;
  const [viewRange, setViewRange] = useState<ViewRange | null>(null);

  const history = historyQuery.data?.points ?? NONE;
  const withoutPlan = projectionQuery.data?.withoutPlan ?? NONE;
  const plan = runQuery.data?.plan ?? null;

  const dataSpan = useMemo<ViewRange>(() => {
    const dates = [
      ...history.map((p) => parseDate(p.date).getTime()),
      ...withoutPlan.map((p) => parseDate(p.date).getTime()),
    ];
    if (dates.length === 0) {
      const now = Date.now();
      return { min: now - YEAR_MS, max: now };
    }
    return { min: Math.min(...dates), max: Math.max(...dates) };
  }, [history, withoutPlan]);

  // Default visible window: one year ending at the projection horizon, so the
  // chart opens on the forecast + recent history and pans left into the rest of
  // the loaded span. Clamped so it never starts before the loaded data.
  const defaultView = useMemo<ViewRange>(() => {
    const projDates = withoutPlan.map((p) => parseDate(p.date).getTime());
    if (projDates.length === 0) return dataSpan;
    const projEnd = Math.max(...projDates);
    return { min: Math.max(projEnd - YEAR_MS, dataSpan.min), max: projEnd };
  }, [withoutPlan, dataSpan]);

  return {
    historyQuery,
    projectionQuery,
    notPlanned: runQuery.data === null,
    history,
    withoutPlan,
    plan,
    dataSpan,
    defaultView,
    viewRange,
    setViewRange,
  };
}
